import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Layout from './layout';
import { vlsEmailsCsv, csvEmail, fromEmails, sendVlsEmail, updateEmailsData, deleteEmailsData } from './proxyurls';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';

function VlsEmails() {
  const [csvRecords, setCsvRecords] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [showSendEmailModal, setShowSendEmailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [fromEmailsList, setFromEmailsList] = useState([]);
  const [emailFormData, setEmailFormData] = useState({
    fromEmail: '',
    subject: '',
    message: ''
  });
  const [editingRecord, setEditingRecord] = useState(null);
  const [editFormData, setEditFormData] = useState({ names: '', emails: '' });
  const [deletingRecordId, setDeletingRecordId] = useState(null);
  const [isSourceView, setIsSourceView] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [sourceCode, setSourceCode] = useState('');
  const [editorKey, setEditorKey] = useState(0); // Key to force CKEditor remount
  
  const editorRef = useRef(null);
  const sourceTextareaRef = useRef(null);
  const preservedTableAttributesRef = useRef({});
  const preservedCellStylesRef = useRef({}); // Store cell (td/th/tr) styles
  const originalHtmlWithAttributesRef = useRef('');
  const onChangeTimeoutRef = useRef(null); // For debouncing onChange operations
  const editorInitializedRef = useRef(false); // Track if editor has been initialized
  

  // Fetch CSV records
  const fetchCsvRecords = async () => {
    try {
      setLoading(true);
      const response = await axios.get(csvEmail);
      console.log("response.data", response.data);
      setCsvRecords(response.data.data || []);
    } catch (error) {
      console.error('Error fetching CSV records:', error);
      setErrorMessage('Error fetching CSV records');
      setTimeout(() => setErrorMessage(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCsvRecords();
    
    // Cleanup function to clear timeout on unmount
    return () => {
      if (onChangeTimeoutRef.current) {
        clearTimeout(onChangeTimeoutRef.current);
        onChangeTimeoutRef.current = null;
      }
    };
  }, []);

  // Preserve table attributes and cell styles when message changes (e.g., when modal opens with existing content)
  useEffect(() => {
    if (emailFormData.message && !isSourceView) {
      // Only preserve if we don't already have preserved attributes
      // This prevents overwriting when user is actively editing
      if (Object.keys(preservedTableAttributesRef.current).length === 0) {
        preservedTableAttributesRef.current = preserveTableAttributes(emailFormData.message);
        preservedCellStylesRef.current = preserveCellStyles(emailFormData.message);
        originalHtmlWithAttributesRef.current = emailFormData.message;
      }
    }
  }, [emailFormData.message, isSourceView]);

  // Handle file selection
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  // Upload CSV file
  const handleUploadCsv = async () => {
    if (!selectedFile) {
      setErrorMessage('Please select a file first');
      setTimeout(() => setErrorMessage(''), 3000);
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await axios.post(vlsEmailsCsv, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        setSuccessMessage('CSV file uploaded successfully');
        setTimeout(() => setSuccessMessage(''), 3000);
        setSelectedFile(null);
        // Reset file input
        document.getElementById('csvFileInput').value = '';
        // Refresh records
        fetchCsvRecords();
      } else {
        setErrorMessage(response.data.message || 'Upload failed');
        setTimeout(() => setErrorMessage(''), 3000);
      }
    } catch (error) {
      console.error('Error uploading CSV:', error);
      setErrorMessage('Error uploading CSV file');
      setTimeout(() => setErrorMessage(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  // Download template (placeholder - you may need to implement this)
  const handleDownloadTemplate = () => {
    // Create a simple CSV template
    const template = 'Name,Email\nJohn Doe,john@example.com\nJane Smith,jane@example.com';
    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'email_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Handle edit record - open modal
  const handleEdit = (record) => {
    console.log('Editing record:', record);
    setEditingRecord(record);
    setEditFormData({ names: record.names || '', emails: record.emails || '' });
    setShowEditModal(true);
  };

  // Handle save edit - update via API
  const handleSaveEdit = async (e) => {
    e.preventDefault();

    // Check for various possible ID field names
    const recordId = editingRecord?.emails_data_id || editingRecord?.emails_data_id || editingRecord?.ID;

    if (!editingRecord || !recordId) {
      console.error('Invalid record - no ID found:', editingRecord);
      setErrorMessage('Invalid record - ID not found');
      setTimeout(() => setErrorMessage(''), 3000);
      return;
    }

    try {
      setLoading(true);
      console.log('Updating record with ID:', recordId, 'Data:', editFormData);
      const response = await axios.put(updateEmailsData(recordId), {
        names: editFormData.names,
        emails: editFormData.emails
      });

      if (response.data.success) {
        setSuccessMessage('Record updated successfully');
        setTimeout(() => setSuccessMessage(''), 3000);
        setShowEditModal(false);
        setEditingRecord(null);
        setEditFormData({ names: '', emails: '' });
        // Refresh records
        fetchCsvRecords();
      } else {
        setErrorMessage(response.data.message || 'Failed to update record');
        setTimeout(() => setErrorMessage(''), 3000);
      }
    } catch (error) {
      console.error('Error updating record:', error);
      setErrorMessage('Error updating record');
      setTimeout(() => setErrorMessage(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  // Handle delete record - open confirmation modal
  const handleDelete = (id) => {
    console.log('Deleting record:', id);
    setDeletingRecordId(id);
    setShowDeleteModal(true);
  };

  // Confirm delete - delete via API
  const handleConfirmDelete = async () => {
    if (!deletingRecordId) return;

    try {
      setLoading(true);
      const response = await axios.delete(deleteEmailsData(deletingRecordId));

      if (response.data.success) {
        setSuccessMessage('Record deleted successfully');
        setTimeout(() => setSuccessMessage(''), 3000);
        setShowDeleteModal(false);
        setDeletingRecordId(null);
        // Refresh records
        fetchCsvRecords();
      } else {
        setErrorMessage(response.data.message || 'Failed to delete record');
        setTimeout(() => setErrorMessage(''), 3000);
      }
    } catch (error) {
      console.error('Error deleting record:', error);
      setErrorMessage('Error deleting record');
      setTimeout(() => setErrorMessage(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  // Open send email modal
  const handleOpenSendEmailModal = async () => {
    try {
      const response = await axios.get(fromEmails);
      setFromEmailsList(response.data.data || []);
      if (response.data && response.data.length > 0) {
        setEmailFormData(prev => ({
          ...prev,
          fromEmail: response.data[0].email || response.data[0].id || ''
        }));
      }
      // Initialize sourceCode with current message if it exists
      // This ensures source view has the exact HTML with all attributes
      if (emailFormData.message) {
        setSourceCode(emailFormData.message);
        preservedTableAttributesRef.current = preserveTableAttributes(emailFormData.message);
        preservedCellStylesRef.current = preserveCellStyles(emailFormData.message);
        originalHtmlWithAttributesRef.current = emailFormData.message;
      } else {
        // Reset sourceCode when opening modal with empty message
        setSourceCode('');
        preservedTableAttributesRef.current = {};
        preservedCellStylesRef.current = {};
        originalHtmlWithAttributesRef.current = '';
      }
      // Reset editor by incrementing key (forces remount)
      setEditorKey(prev => prev + 1);
      editorInitializedRef.current = false;
      // Reset source view to false when opening modal
      setIsSourceView(false);
      setShowSendEmailModal(true);
    } catch (error) {
      console.error('Error fetching from emails:', error);
      setErrorMessage('Error loading from emails');
      setTimeout(() => setErrorMessage(''), 3000);
    }
  };

  // Handle send email form submit
  const handleSendEmail = async (e) => {
    e.preventDefault();

    console.log('🚀 Starting email send process...');
    console.log('📊 Current state:');
    console.log('  - isSourceView:', isSourceView);
    console.log('  - sourceCode:', sourceCode ? sourceCode.substring(0, 200) : 'empty');
    console.log('  - emailFormData.message:', emailFormData.message ? emailFormData.message.substring(0, 200) : 'empty');
    console.log('  - originalHtmlWithAttributesRef:', originalHtmlWithAttributesRef.current ? originalHtmlWithAttributesRef.current.substring(0, 200) : 'empty');
    console.log('  - preservedTableAttributesRef:', preservedTableAttributesRef.current);

    // CRITICAL: Priority should be current editor state when in visual mode
    // If user is in visual mode, get current editor content first (may have been edited)
    let messageWithAttributes = '';
    
    if (isSourceView) {
      // In source view, use sourceCode or emailFormData.message
      if (sourceCode && sourceCode.trim() !== '') {
        console.log('✅ Source view: Using sourceCode');
        messageWithAttributes = sourceCode;
      } else {
        console.log('✅ Source view: Using emailFormData.message');
        messageWithAttributes = emailFormData.message || '';
      }
    } else {
      // In visual mode: Always get current editor content first (user may have edited)
      console.log('✅ Visual view: Getting current editor content');
      
      // Priority 1: Get current editor data (most up-to-date)
      if (editorRef.current) {
        const currentEditorData = editorRef.current.getData();
        console.log('📝 Current editor data:', currentEditorData.substring(0, 200));
        console.log('📏 Current editor data length:', currentEditorData.length);
        
        // Restore attributes and cell styles from preserved data (CRITICAL: use preserved attributes, don't overwrite them)
        let restoredData = restoreTableAttributes(currentEditorData, preservedTableAttributesRef.current, preservedCellStylesRef.current);
        console.log('🔄 After restoring attributes:', restoredData.substring(0, 200));
        
        // Verify that critical attributes like 'border' are present after restoration
        const tablesInRestored = (restoredData.match(/<table[^>]*>/gi) || []).length;
        if (tablesInRestored > 0 && Object.keys(preservedTableAttributesRef.current).length > 0) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(restoredData, 'text/html');
          const tables = doc.querySelectorAll('table');
          tables.forEach((table, index) => {
            const key = `table_${index}`;
            const preserved = preservedTableAttributesRef.current[key];
            if (preserved && preserved.border) {
              if (!table.getAttribute('border')) {
                console.warn(`⚠️ Border attribute missing for table ${index} after restoration! Adding it...`);
                table.setAttribute('border', preserved.border);
              }
            }
          });
          // Get updated HTML with border attributes
          restoredData = doc.body.innerHTML;
          console.log('✅ Re-verified and ensured border attributes are present');
        }
        
        // Don't overwrite preserved attributes - they are the source of truth from original HTML
        // Only update preserved attributes if we find NEW attributes that weren't there before
        const currentPreserved = preserveTableAttributes(restoredData);
        Object.keys(currentPreserved).forEach(key => {
          if (!preservedTableAttributesRef.current[key]) {
            // New table, add its attributes
            preservedTableAttributesRef.current[key] = currentPreserved[key];
          } else {
            // Existing table - merge but prioritize original preserved attributes
            const existing = preservedTableAttributesRef.current[key];
            preservedTableAttributesRef.current[key] = { ...existing, ...currentPreserved[key] };
          }
        });
        
        messageWithAttributes = restoredData;
      }
      // Priority 2: Use emailFormData.message (updated by onChange handler)
      else if (emailFormData.message && emailFormData.message.trim() !== '') {
        console.log('⚠️ No editor ref, using emailFormData.message');
        messageWithAttributes = emailFormData.message;
      }
      // Priority 3: Fall back to original HTML or sourceCode
      else if (originalHtmlWithAttributesRef.current && originalHtmlWithAttributesRef.current.trim() !== '') {
        console.log('⚠️ Fallback: Using originalHtmlWithAttributesRef');
        messageWithAttributes = originalHtmlWithAttributesRef.current;
      }
      else if (sourceCode && sourceCode.trim() !== '') {
        console.log('⚠️ Fallback: Using sourceCode');
        messageWithAttributes = sourceCode;
      }
      else {
        console.log('⚠️ No content available');
        messageWithAttributes = '';
      }
    }
    
    // Final restoration pass: Ensure all preserved attributes are applied
    // This is a safety net - even if we used original HTML, make sure attributes are there
    if (Object.keys(preservedTableAttributesRef.current).length > 0) {
      // Check if message already has attributes
      const messagePreserved = preserveTableAttributes(messageWithAttributes);
      const messageHasAttributes = Object.keys(messagePreserved).length > 0;
      
      if (!messageHasAttributes) {
        console.log('⚠️ Message has no attributes but we have preserved attributes - forcing restoration');
        const finalRestored = restoreTableAttributes(messageWithAttributes, preservedTableAttributesRef.current, preservedCellStylesRef.current);
        if (finalRestored !== messageWithAttributes) {
          console.log('✅ Final restoration pass added attributes to message');
          messageWithAttributes = finalRestored;
        }
      } else {
        // Message has attributes, but verify they match preserved attributes
        console.log('✅ Message already has attributes, verifying they match preserved attributes');
        const finalRestored = restoreTableAttributes(messageWithAttributes, preservedTableAttributesRef.current, preservedCellStylesRef.current);
        // Use restored version if it's different (might have more attributes)
        if (finalRestored !== messageWithAttributes) {
          const restoredPreserved = preserveTableAttributes(finalRestored);
          if (Object.keys(restoredPreserved).length > Object.keys(messagePreserved).length) {
            console.log('✅ Restored version has more attributes, using it');
            messageWithAttributes = finalRestored;
          }
        }
      }
    }
    
    // Verify tables have attributes before sending
    const tablesInMessage = (messageWithAttributes.match(/<table[^>]*>/gi) || []).length;
    const preservedTables = Object.keys(preservedTableAttributesRef.current).length;
    console.log(`📊 Verification: Found ${tablesInMessage} table(s) in message, ${preservedTables} preserved attribute set(s)`);
    
    if (tablesInMessage > 0 && preservedTables > 0) {
      // Check if tables have attributes
      const parser = new DOMParser();
      const doc = parser.parseFromString(messageWithAttributes, 'text/html');
      const tables = doc.querySelectorAll('table');
      let tablesWithAttributes = 0;
      tables.forEach((table, index) => {
        const hasAttributes = table.attributes.length > 0;
        if (hasAttributes) {
          tablesWithAttributes++;
          const attrList = Array.from(table.attributes).map(a => `${a.name}="${a.value}"`).join(', ');
          console.log(`✅ Table ${index} has ${table.attributes.length} attribute(s): ${attrList}`);
        } else {
          console.log(`⚠️ Table ${index} has NO attributes - this is a problem!`);
        }
      });
      console.log(`📊 ${tablesWithAttributes}/${tables.length} tables have attributes`);
      
      // Run verification
      const verification = verifyTableAttributes(messageWithAttributes, preservedTableAttributesRef.current);
      if (!verification.verified) {
        console.warn('⚠️ Verification failed:', verification.message);
        if (verification.details) {
          verification.details.forEach(detail => {
            console.warn(`  Table ${detail.tableIndex} missing: ${detail.missing.join(', ')}`);
            console.warn(`  Expected:`, detail.expected);
            console.warn(`  Actual:`, detail.actual);
          });
        }
        
        // Try one more restoration attempt
        console.log('🔄 Attempting additional restoration...');
        messageWithAttributes = restoreTableAttributes(messageWithAttributes, preservedTableAttributesRef.current, preservedCellStylesRef.current);
        
        // Re-verify
        const reVerification = verifyTableAttributes(messageWithAttributes, preservedTableAttributesRef.current);
        if (reVerification.verified) {
          console.log('✅ Re-verification passed after additional restoration');
        } else {
          console.warn('⚠️ Re-verification still failed:', reVerification.message);
        }
      } else {
        console.log('✅ Verification passed:', verification.message);
      }
    }
    
    // Check if message has content (not just empty HTML tags)
    const messageContent = messageWithAttributes.replace(/<[^>]*>/g, '').trim();
    if (!messageContent) {
      setErrorMessage('Please enter a message');
      setTimeout(() => setErrorMessage(''), 3000);
      return;
    }

    // Log the final message being sent
    console.log('📧 FINAL MESSAGE TO SEND (first 1000 chars):');
    console.log(messageWithAttributes.substring(0, 1000));
    console.log('📧 FINAL MESSAGE TO SEND (full length):', messageWithAttributes.length, 'characters');

    try {
      setLoading(true);
      const response = await axios.post(sendVlsEmail, {
        fromEmail: emailFormData.fromEmail,
        subject: emailFormData.subject,
        message: messageWithAttributes, // Exact HTML with all attributes preserved
        recipients: csvRecords.map(record => record.emails)
      });

      if (response.data.success) {
        setSuccessMessage('Email sent successfully');
        setTimeout(() => setSuccessMessage(''), 3000);
        setShowSendEmailModal(false);
        setEmailFormData({ fromEmail: '', subject: '', message: '' });
        setSourceCode('');
        setIsSourceView(false);
        preservedTableAttributesRef.current = {};
        preservedCellStylesRef.current = {};
        originalHtmlWithAttributesRef.current = '';
        // Refresh records
        fetchCsvRecords();
      } else {
        setErrorMessage(response.data.message || 'Failed to send email');
        setTimeout(() => setErrorMessage(''), 3000);
      }
    } catch (error) {
      console.error('Error sending email:', error);
      setErrorMessage('Error sending email');
      setTimeout(() => setErrorMessage(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  // Custom upload adapter for base64 image embedding (no API call)
  const uploadAdapterPlugin = (editor) => {
    try {
      // Check if FileRepository plugin is available
      const fileRepository = editor.plugins.get('FileRepository');
      if (!fileRepository) {
        console.error('FileRepository plugin not found. Image upload may not work.');
        return;
      }

      // Create custom upload adapter
      fileRepository.createUploadAdapter = (loader) => {
        return new (class {
          constructor(loader) {
            this.loader = loader;
          }

          upload() {
            return this.loader.file.then(file => {
              // Validate file size (60KB = 60 * 1024 bytes)
              const maxSize = 60 * 1024; // 60KB in bytes
              if (file.size > maxSize) {
                const errorMsg = `Image size must be less than 60KB. Current size: ${(file.size / 1024).toFixed(2)}KB`;
                setErrorMessage(errorMsg);
                setTimeout(() => setErrorMessage(''), 5000);
                return Promise.reject(new Error(errorMsg));
              }

              // Validate file type
              if (!file.type.match(/^image\/(jpeg|jpg|png|gif|webp)$/)) {
                const errorMsg = 'Invalid image format. Please use JPEG, PNG, GIF, or WebP';
                setErrorMessage(errorMsg);
                setTimeout(() => setErrorMessage(''), 5000);
                return Promise.reject(new Error(errorMsg));
              }

              // Convert image to base64 data URL (direct embedding in content)
              return new Promise((resolve, reject) => {
                const reader = new FileReader();
                
                reader.onload = () => {
                  // Resolve with base64 data URL - image will be embedded directly in HTML
                  resolve({
                    default: reader.result
                  });
                  setSuccessMessage('Image added successfully');
                  setTimeout(() => setSuccessMessage(''), 3000);
                };
                
                reader.onerror = () => {
                  const errorMsg = 'Error reading image file';
                  setErrorMessage(errorMsg);
                  setTimeout(() => setErrorMessage(''), 5000);
                  reject(new Error(errorMsg));
                };
                
                reader.readAsDataURL(file);
              });
            });
          }

          abort() {
            // Handle upload cancellation if needed
          }
        })(loader);
      };
      console.log('Upload adapter successfully configured');
    } catch (error) {
      console.error('Error setting up upload adapter:', error);
      setErrorMessage('Image upload feature not available. Please check browser console for details.');
      setTimeout(() => setErrorMessage(''), 5000);
    }
  };

  // CKEditor configuration
  const editorConfiguration = {
    toolbar: [
      'heading',
      '|',
      'bold',
      'italic',
      'underline',
      'strikethrough',
      '|',
      'bulletedList',
      'numberedList',
      '|',
      'outdent',
      'indent',
      '|',
      'link',
      'image',
      '|',
      'blockQuote',
      'insertTable',
      '|',
      'undo',
      'redo'
    ],
    image: {
      toolbar: [
        'imageTextAlternative',
        'toggleImageCaption',
        'imageStyle:inline',
        'imageStyle:block',
        'imageStyle:side'
      ],
      upload: {
        types: ['jpeg', 'jpg', 'png', 'gif', 'webp']
      },
      insert: {
        integrations: ['upload', 'url']
      }
    }
  };

  // Utility function to extract and preserve ALL table attributes AND cell styles
  const preserveTableAttributes = (html) => {
    if (!html || typeof html !== 'string') {
      return {};
    }
    
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const tables = doc.querySelectorAll('table');
      const preserved = {};
      
      console.log(`🔍 Preserving attributes from ${tables.length} table(s)`);
      
      tables.forEach((table, index) => {
        const key = `table_${index}`;
        const attrs = {};
        
        // Get ALL attributes from the table element
        if (table.attributes && table.attributes.length > 0) {
          for (let i = 0; i < table.attributes.length; i++) {
            const attr = table.attributes[i];
            attrs[attr.name] = attr.value;
          }
        }
        
        // CRITICAL: Preserve common attributes explicitly (priority over parsed attributes)
        // This ensures we capture attributes even if they're set differently
        attrs.border = table.getAttribute('border') || attrs.border || '';
        attrs.cellpadding = table.getAttribute('cellpadding') || attrs.cellpadding || '';
        attrs.cellspacing = table.getAttribute('cellspacing') || attrs.cellspacing || '';
        attrs.width = table.getAttribute('width') || attrs.width || '';
        attrs.align = table.getAttribute('align') || attrs.align || '';
        attrs.style = table.getAttribute('style') || attrs.style || '';
        attrs.class = table.getAttribute('class') || attrs.class || '';
        attrs.id = table.getAttribute('id') || attrs.id || '';
        
        // Also check for border in style attribute and preserve it
        if (!attrs.border && attrs.style) {
          const borderMatch = attrs.style.match(/border\s*:\s*([^;]+)/i);
          if (borderMatch) {
            console.log(`⚠️ Border found in style attribute: ${borderMatch[1]}, preserving in style`);
          }
        }
        
        // Log border specifically to help debug
        if (attrs.border) {
          console.log(`✅ Preserved BORDER attribute for table ${index}: "${attrs.border}"`);
        } else {
          console.log(`⚠️ No border attribute found for table ${index}`);
        }
        
        console.log(`💾 Preserved attributes for table ${index}:`, attrs);
        preserved[key] = attrs;
      });
      
      return preserved;
    } catch (error) {
      console.error('❌ Error preserving table attributes:', error);
      return {};
    }
  };

  // Utility function to preserve ALL cell (td/th/tr) styles
  const preserveCellStyles = (html) => {
    if (!html || typeof html !== 'string') {
      return {};
    }
    
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const tables = doc.querySelectorAll('table');
      const preserved = {};
      
      console.log(`🔍 Preserving cell styles from ${tables.length} table(s)`);
      
      tables.forEach((table, tableIndex) => {
        const tableKey = `table_${tableIndex}`;
        preserved[tableKey] = {
          tds: [],
          ths: [],
          trs: []
        };
        
        // Get all rows
        const rows = table.querySelectorAll('tr');
        rows.forEach((row, rowIndex) => {
          // Preserve row (tr) styles
          const rowStyle = row.getAttribute('style');
          if (rowStyle) {
            preserved[tableKey].trs[rowIndex] = rowStyle;
          }
          
          // Preserve cell (td/th) styles
          const cells = row.querySelectorAll('td, th');
          cells.forEach((cell, cellIndex) => {
            const cellStyle = cell.getAttribute('style');
            if (cellStyle) {
              // Create a unique identifier: rowIndex_cellIndex
              const cellKey = `${rowIndex}_${cellIndex}`;
              if (!preserved[tableKey][cell.tagName.toLowerCase() + 's']) {
                preserved[tableKey][cell.tagName.toLowerCase() + 's'] = [];
              }
              preserved[tableKey][cell.tagName.toLowerCase() + 's'][cellKey] = cellStyle;
              
              // Check for border styles in cells
              if (cellStyle.includes('border')) {
                console.log(`✅ Preserved cell style with border for ${cell.tagName} at row ${rowIndex}, cell ${cellIndex}:`, cellStyle.substring(0, 100));
              }
            }
          });
        });
      });
      
      console.log(`💾 Preserved cell styles:`, preserved);
      return preserved;
    } catch (error) {
      console.error('❌ Error preserving cell styles:', error);
      return {};
    }
  };

  // Utility function to verify tables have attributes
  const verifyTableAttributes = (html, preservedAttributes) => {
    if (!html || typeof html !== 'string') {
      return { verified: false, message: 'No HTML provided' };
    }
    
    if (!preservedAttributes || Object.keys(preservedAttributes).length === 0) {
      return { verified: true, message: 'No attributes to verify' };
    }
    
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const tables = doc.querySelectorAll('table');
      
      const results = [];
      tables.forEach((table, index) => {
        const key = `table_${index}`;
        const expectedAttrs = preservedAttributes[key];
        const actualAttrs = {};
        
        Array.from(table.attributes).forEach(attr => {
          actualAttrs[attr.name] = attr.value;
        });
        
        if (expectedAttrs) {
          const missing = [];
          Object.keys(expectedAttrs).forEach(attrName => {
            if (expectedAttrs[attrName] && expectedAttrs[attrName] !== '' && 
                !actualAttrs[attrName]) {
              missing.push(attrName);
            }
          });
          
          if (missing.length > 0) {
            results.push({
              tableIndex: index,
              missing: missing,
              expected: expectedAttrs,
              actual: actualAttrs
            });
          }
        }
      });
      
      return {
        verified: results.length === 0,
        message: results.length === 0 ? 'All attributes verified' : `${results.length} table(s) missing attributes`,
        details: results
      };
    } catch (error) {
      return { verified: false, message: `Error: ${error.message}` };
    }
  };

  // Utility function to restore ALL table attributes AND cell styles
  const restoreTableAttributes = (html, preservedAttributes, preservedCellStyles = {}) => {
    if (!html || typeof html !== 'string') {
      return html;
    }
    
    const hasTableAttrs = preservedAttributes && Object.keys(preservedAttributes).length > 0;
    const hasCellStyles = preservedCellStyles && Object.keys(preservedCellStyles).length > 0;
    
    if (!hasTableAttrs && !hasCellStyles) {
      console.log('⚠️ No preserved attributes or cell styles to restore');
      return html;
    }

    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const tables = doc.querySelectorAll('table');
      
      console.log(`🔍 Found ${tables.length} table(s) in HTML`);
      if (hasTableAttrs) {
        console.log(`💾 Have ${Object.keys(preservedAttributes).length} preserved attribute set(s)`);
      }
      if (hasCellStyles) {
        console.log(`💾 Have ${Object.keys(preservedCellStyles).length} preserved cell style set(s)`);
      }
      
      // Restore ALL attributes and styles for each table
      tables.forEach((table, index) => {
        const key = `table_${index}`;
        
        // Restore table attributes
        if (hasTableAttrs) {
          const attrs = preservedAttributes[key];
          
          if (attrs) {
            console.log(`✅ Restoring attributes for table ${index}:`, attrs);
            // First, remove all existing attributes to avoid conflicts
            // (We'll restore them from preserved data)
            Array.from(table.attributes).forEach(attr => {
              table.removeAttribute(attr.name);
            });
            
            // Restore ALL preserved attributes
            Object.keys(attrs).forEach(attrName => {
              const attrValue = attrs[attrName];
              // Set attributes that have non-empty values
              // CRITICAL: For 'border' attribute, also accept '0' as a valid value (means no border but attribute exists)
              // Skip null, undefined, and empty strings (empty strings mean attribute was not set originally)
              if (attrValue !== null && attrValue !== undefined && attrValue !== '') {
                table.setAttribute(attrName, attrValue);
                // Special logging for border attribute
                if (attrName === 'border') {
                  console.log(`  → ✅ Set BORDER="${attrValue}" (CRITICAL ATTRIBUTE)`);
                } else {
                  console.log(`  → Set ${attrName}="${attrValue}"`);
                }
              } else if (attrName === 'border' && attrValue === '0') {
                // Border='0' is valid - means no border but attribute is present
                table.setAttribute(attrName, '0');
                console.log(`  → Set ${attrName}="0" (explicit no border)`);
              } else {
                console.log(`  ⏭️ Skipping ${attrName} (value: "${attrValue}")`);
              }
            });
            
            // Verify attributes were set, especially border
            const setAttributes = Array.from(table.attributes).map(a => `${a.name}="${a.value}"`).join(', ');
            const hasBorder = table.getAttribute('border');
            if (attrs.border && !hasBorder) {
              console.error(`❌ ERROR: Border attribute was NOT restored! Expected: "${attrs.border}"`);
              // Force restore border
              table.setAttribute('border', attrs.border);
              console.log(`✅ Force-restored border="${attrs.border}"`);
            } else if (attrs.border && hasBorder) {
              console.log(`  ✅ Verified BORDER attribute is present: "${hasBorder}"`);
            }
            console.log(`  ✅ Table ${index} now has attributes: ${setAttributes}`);
          } else {
            console.log(`⚠️ No preserved attributes found for table ${index} (key: ${key})`);
            console.log(`  Available keys: ${Object.keys(preservedAttributes).join(', ')}`);
          }
        }
        
        // Restore cell styles (td/th/tr)
        if (hasCellStyles) {
          const cellStyles = preservedCellStyles[key];
          if (cellStyles) {
            console.log(`✅ Restoring cell styles for table ${index}`);
            
            const rows = table.querySelectorAll('tr');
            rows.forEach((row, rowIndex) => {
              // Restore row (tr) styles
              if (cellStyles.trs && cellStyles.trs[rowIndex]) {
                row.setAttribute('style', cellStyles.trs[rowIndex]);
                console.log(`  → Restored TR style for row ${rowIndex}`);
              }
              
              // Restore cell (td/th) styles
              const cells = row.querySelectorAll('td, th');
              cells.forEach((cell, cellIndex) => {
                const cellKey = `${rowIndex}_${cellIndex}`;
                const cellTag = cell.tagName.toLowerCase();
                const cellStyleArray = cellStyles[cellTag + 's'];
                
                if (cellStyleArray && cellStyleArray[cellKey]) {
                  cell.setAttribute('style', cellStyleArray[cellKey]);
                  if (cellStyleArray[cellKey].includes('border')) {
                    console.log(`  → ✅ Restored ${cellTag.toUpperCase()} style with border for row ${rowIndex}, cell ${cellIndex}`);
                  } else {
                    console.log(`  → Restored ${cellTag.toUpperCase()} style for row ${rowIndex}, cell ${cellIndex}`);
                  }
                }
              });
            });
            
            console.log(`  ✅ Finished restoring cell styles for table ${index}`);
          }
        }
      });
      
      const restoredHtml = doc.body.innerHTML;
      console.log('📋 Restored HTML:', restoredHtml.substring(0, 200));
      return restoredHtml;
    } catch (error) {
      console.error('❌ Error restoring table attributes:', error);
      return html;
    }
  };

  // Get the current message with attributes preserved
  const getMessageWithAttributes = () => {
    console.log('📧 Getting message with attributes for email send...');
    console.log('  - isSourceView:', isSourceView);
    console.log('  - sourceCode length:', sourceCode ? sourceCode.length : 0);
    console.log('  - emailFormData.message length:', emailFormData.message ? emailFormData.message.length : 0);
    console.log('  - preservedTableAttributesRef:', preservedTableAttributesRef.current);
    console.log('  - originalHtmlWithAttributesRef:', originalHtmlWithAttributesRef.current ? originalHtmlWithAttributesRef.current.substring(0, 100) : 'empty');
    
    if (isSourceView) {
      // In source mode, ALWAYS use sourceCode directly - it contains the exact HTML with all attributes
      // This ensures that when user pastes HTML in source view, all attributes are preserved
      const result = sourceCode || emailFormData.message || '';
      console.log('✅ Using sourceCode (source view):', result.substring(0, 200));
      return result;
    } else {
      // In visual mode, try multiple strategies to get the best version with attributes
      let finalMessage = '';
      
      // Strategy 1: Use original HTML with attributes if available (most reliable)
      if (originalHtmlWithAttributesRef.current && originalHtmlWithAttributesRef.current.trim() !== '') {
        console.log('✅ Strategy 1: Using original HTML with attributes');
        finalMessage = originalHtmlWithAttributesRef.current;
      }
      // Strategy 2: Restore attributes from editor data
      else if (editorRef.current) {
        const data = editorRef.current.getData();
        console.log('📋 Editor data:', data.substring(0, 200));
        const restored = restoreTableAttributes(data, preservedTableAttributesRef.current);
        console.log('✅ Strategy 2: Restored from editor:', restored.substring(0, 200));
        finalMessage = restored;
      }
      // Strategy 3: Use sourceCode if available
      else if (sourceCode && sourceCode.trim() !== '') {
        console.log('✅ Strategy 3: Using sourceCode');
        finalMessage = sourceCode;
      }
      // Strategy 4: Fall back to emailFormData.message
      else {
        console.log('✅ Strategy 4: Using emailFormData.message');
        finalMessage = emailFormData.message || '';
      }
      
      // Final check: If we still don't have attributes and we have preserved attributes, try one more restoration
      if (finalMessage && Object.keys(preservedTableAttributesRef.current).length > 0) {
        const finalRestored = restoreTableAttributes(finalMessage, preservedTableAttributesRef.current);
        if (finalRestored !== finalMessage) {
          console.log('✅ Final restoration improved the message');
          finalMessage = finalRestored;
        }
      }
      
      console.log('📧 Final message to send:', finalMessage.substring(0, 500));
      return finalMessage;
    }
  };

  // Source code toggle handler
  const toggleSourceView = () => {
    if (isSourceView) {
      // Switching from source to visual
      console.log('🔄 Switching from SOURCE to VISUAL');
      console.log('📋 Source code BEFORE switch:', sourceCode);
      
      if (editorRef.current && sourceCode) {
        // Store original HTML with attributes (this is the source of truth)
        originalHtmlWithAttributesRef.current = sourceCode;
        
        // Preserve attributes and cell styles from source code BEFORE setting in editor
        const preserved = preserveTableAttributes(sourceCode);
        const preservedCellStyles = preserveCellStyles(sourceCode);
        preservedTableAttributesRef.current = preserved;
        preservedCellStylesRef.current = preservedCellStyles;
        
        console.log('💾 Preserved attributes:', preserved);
        console.log('📝 Original HTML with attributes stored:', originalHtmlWithAttributesRef.current);
        
        // Set the data - CKEditor will strip attributes, but we've preserved them
        editorRef.current.setData(sourceCode);
        
        // Get what CKEditor actually stored (will be stripped)
        setTimeout(() => {
          const editorData = editorRef.current.getData();
          console.log('⚠️ CKEditor data (attributes stripped):', editorData);
          console.log('✅ But we have preserved attributes:', preservedTableAttributesRef.current);
        }, 100);
        
        // Store the version WITH attributes in emailFormData
        setEmailFormData(prev => ({ ...prev, message: sourceCode }));
      }
      setIsSourceView(false);
    } else {
      // Switching from visual to source
      console.log('🔄 Switching from VISUAL to SOURCE');
      
      if (editorRef.current) {
        const html = editorRef.current.getData();
        console.log('📋 Editor HTML (may have stripped attributes):', html);
        console.log('💾 Preserved attributes available:', preservedTableAttributesRef.current);
        console.log('📝 Original HTML with attributes:', originalHtmlWithAttributesRef.current);
        
        // PRIORITY 1: If we have original HTML with attributes, use it directly
        // This is the most reliable source since it's what the user originally pasted
        if (originalHtmlWithAttributesRef.current && originalHtmlWithAttributesRef.current.trim() !== '') {
          console.log('✅ Using original HTML with attributes (most reliable)');
          const originalHtml = originalHtmlWithAttributesRef.current;
          
          // Update preserved attributes and cell styles from original
          preservedTableAttributesRef.current = preserveTableAttributes(originalHtml);
          preservedCellStylesRef.current = preserveCellStyles(originalHtml);
          
          setSourceCode(originalHtml);
          setEmailFormData(prev => ({ ...prev, message: originalHtml }));
        } else {
          // PRIORITY 2: Try to restore attributes and cell styles from preserved data
          let htmlWithAttributes = restoreTableAttributes(html, preservedTableAttributesRef.current, preservedCellStylesRef.current);
          
          // PRIORITY 3: If restoration didn't work and we have sourceCode with attributes, use that
          if (htmlWithAttributes === html && sourceCode && sourceCode !== html) {
            console.log('⚠️ Restoration didn\'t work, trying sourceCode');
            const sourcePreserved = preserveTableAttributes(sourceCode);
            const sourceCellStyles = preserveCellStyles(sourceCode);
            if (Object.keys(sourcePreserved).length > 0 || Object.keys(sourceCellStyles).length > 0) {
              htmlWithAttributes = restoreTableAttributes(html, sourcePreserved, sourceCellStyles);
              preservedTableAttributesRef.current = sourcePreserved;
              preservedCellStylesRef.current = sourceCellStyles;
            } else {
              // If sourceCode has attributes in the HTML itself, use it directly
              htmlWithAttributes = sourceCode;
            }
          }
          
          console.log('✅ Final HTML with attributes:', htmlWithAttributes);
          
          // Update preserved attributes and cell styles from the restored HTML
          preservedTableAttributesRef.current = preserveTableAttributes(htmlWithAttributes);
          preservedCellStylesRef.current = preserveCellStyles(htmlWithAttributes);
          
          setSourceCode(htmlWithAttributes);
          // Also update emailFormData with the version that has attributes
          setEmailFormData(prev => ({ ...prev, message: htmlWithAttributes }));
        }
      }
      setIsSourceView(true);
    }
  };

  // Fullscreen toggle handler
  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  // Handle source code textarea change
  const handleSourceCodeChange = (e) => {
    const newSourceCode = e.target.value;
    console.log('📝 Source code changed:', newSourceCode.substring(0, 200));
    
    // Always preserve table attributes and cell styles when editing in source mode
    // This ensures ALL attributes and styles are captured from the source HTML
    const preserved = preserveTableAttributes(newSourceCode);
    const preservedCellStyles = preserveCellStyles(newSourceCode);
    preservedTableAttributesRef.current = preserved;
    preservedCellStylesRef.current = preservedCellStyles;
    originalHtmlWithAttributesRef.current = newSourceCode;
    
    console.log('💾 Preserved attributes from source:', preserved);
    
    setSourceCode(newSourceCode);
    // Always store the version WITH attributes in emailFormData
    // This is the exact HTML the user typed/pasted, preserving all attributes
    setEmailFormData(prev => ({ ...prev, message: newSourceCode }));
  };

  return (
    <Layout>
      {successMessage && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          {successMessage}
          <button type="button" className="btn-close" onClick={() => setSuccessMessage('')}></button>
        </div>
      )}
      {errorMessage && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {errorMessage}
          <button type="button" className="btn-close" onClick={() => setErrorMessage('')}></button>
        </div>
      )}

      <section className="w-100 p-3">
        <div style={{ padding: '14px' }}>
          {/* Import CSV Section */}
          <div style={{ backgroundColor: '#1e3a8a', padding: '20px', borderRadius: '8px', marginBottom: '20px' }}>
            <h3 style={{ color: '#fff', marginBottom: '15px' }}>Import CSV Files Here</h3>
            <h5 style={{ color: '#fff', marginBottom: '20px' }}>Import File</h5>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => document.getElementById('csvFileInput').click()}
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <i className="fa fa-file"></i> Choose File
              </button>
              <input
                type="file"
                id="csvFileInput"
                accept=".csv"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
              {selectedFile && (
                <span style={{ color: '#fff', alignSelf: 'center' }}>{selectedFile.name}</span>
              )}
              <button
                type="button"
                className="btn btn-success"
                onClick={handleDownloadTemplate}
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <i className="fa fa-download"></i>
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleUploadCsv}
                disabled={loading || !selectedFile}
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                {loading ? 'Uploading...' : 'Upload CSV File'}
              </button>
            </div>
          </div>

          {/* CSV Records Table */}
          <div style={{ backgroundColor: '#fff', borderRadius: '8px', overflow: 'hidden' }}>
            <div style={{ backgroundColor: '#1e3a8a', padding: '15px' }}>
              <h4 style={{ color: '#fff', margin: 0 }}>All Names, Emails Imported List</h4>
            </div>
            <div className="table-responsive">
              <table className="table table-bordered table-hover mb-0">
                <thead style={{ backgroundColor: '#f8f9fa' }}>
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && csvRecords.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="text-center">Loading...</td>
                    </tr>
                  ) : csvRecords.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="text-center">No records found</td>
                    </tr>
                  ) : (
                    csvRecords.map((record, index) => (
                      <tr key={record.id || index}>
                        <td>{index + 1}</td>
                        <td>{record.names || 'N/A'}</td>
                        <td>{record.emails || 'N/A'}</td>
                        <td>
                          <div style={{ display: 'flex', gap: '5px' }}>
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => handleEdit(record)}
                            >
                              <i className="fa fa-pencil"></i>
                            </button>
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => handleDelete(record.emails_data_id || record.emails_data_id || record.ID)}
                            >
                              <i className="fa fa-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {csvRecords.length > 0 && (
              <div style={{ padding: '15px', textAlign: 'center' }}>
                <button
                  type="button"
                  className="btn btn-primary btn-lg"
                  onClick={handleOpenSendEmailModal}
                  disabled={loading}
                >
                  <i className="fa fa-envelope" style={{ marginRight: '8px' }}></i>
                  Send Email
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Edit Record Modal */}
      {showEditModal && (
        <div
          className="modal fade show"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Record</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingRecord(null);
                    setEditFormData({ names: '', emails: '' });
                  }}
                ></button>
              </div>
              <form onSubmit={handleSaveEdit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label htmlFor="editName" className="form-label">Name</label>
                    <input
                      type="text"
                      className="form-control"
                      id="editName"
                      value={editFormData.names}
                      onChange={(e) => setEditFormData({ ...editFormData, names: e.target.value })}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="editEmail" className="form-label">Email</label>
                    <input
                      type="email"
                      className="form-control"
                      id="editEmail"
                      value={editFormData.emails}
                      onChange={(e) => setEditFormData({ ...editFormData, emails: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowEditModal(false);
                      setEditingRecord(null);
                      setEditFormData({ names: '', emails: '' });
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? 'Updating...' : 'Update'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div
          className="modal fade show"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Delete</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeletingRecordId(null);
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete this record? This action cannot be undone.</p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeletingRecordId(null);
                  }}
                >
                  No
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleConfirmDelete}
                  disabled={loading}
                >
                  {loading ? 'Deleting...' : 'Yes, Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Send Email Modal */}
      {showSendEmailModal && (
        <div
          className="modal fade show"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: '0' }}>
              <div className="modal-header" style={{ backgroundColor: '#1e3a8a', color: '#fff', borderBottom: 'none', padding: '15px 20px' }}>
                <h5 className="modal-title" style={{ margin: 0, fontWeight: 'bold' }}>Email CSV Compose</h5>
                <button
                  type="button"
                  className="btn-close btn-close-black"
                  style={{ backgroundColor: '#e9e9e9', borderRadius: '50%', width: '25px', height: '25px', opacity: '1', padding: '0' }}
                  onClick={() => {
                    // Clear any pending timeout
                    if (onChangeTimeoutRef.current) {
                      clearTimeout(onChangeTimeoutRef.current);
                      onChangeTimeoutRef.current = null;
                    }
                    setShowSendEmailModal(false);
                    setEmailFormData({ fromEmail: '', subject: '', message: '' });
                    setSourceCode('');
                    setIsSourceView(false);
                    editorInitializedRef.current = false;
                    preservedTableAttributesRef.current = {};
                    preservedCellStylesRef.current = {};
                    originalHtmlWithAttributesRef.current = '';
                  }}
                ></button>
              </div>
              <form onSubmit={handleSendEmail}>
                <div className="modal-body" style={{ padding: '20px' }}>
                  <div className="mb-3">
                    <label htmlFor="fromEmail" className="form-label" style={{ fontWeight: '500', marginBottom: '8px' }}>Email Address</label>
                    <select
                      className="form-select"
                      id="fromEmail"
                      value={emailFormData.fromEmail}
                      onChange={(e) => setEmailFormData({ ...emailFormData, fromEmail: e.target.value })}
                      required
                      style={{ padding: '8px 12px' }}
                    >
                      <option value="">Select Email Address</option>
                      {fromEmailsList.map((email, index) => (
                        <option key={index} value={email.email || email.id || email}>
                          {email.email || email.name || email}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="mb-3">
                    <label htmlFor="subject" className="form-label" style={{ fontWeight: '500', marginBottom: '8px' }}>Email Subject</label>
                    <input
                      type="text"
                      className="form-control"
                      id="subject"
                      value={emailFormData.subject}
                      onChange={(e) => setEmailFormData({ ...emailFormData, subject: e.target.value })}
                      required
                      style={{ padding: '8px 12px' }}
                    />
                  </div>
                  <div className="mb-3" style={{ position: 'relative' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <label htmlFor="message" className="form-label" style={{ fontWeight: '500', marginBottom: 0 }}>Email Message Body (Upload image size less than 60Kb)</label>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          type="button"
                          className={`btn btn-sm ${isSourceView ? 'btn-primary' : 'btn-outline-secondary'}`}
                          onClick={toggleSourceView}
                          style={{ fontSize: '12px', padding: '4px 8px' }}
                          title={isSourceView ? "Switch to Visual Editor" : "Switch to Source Code"}
                        >
                          <i className="fa fa-code" style={{ marginRight: '4px' }}></i>
                          {isSourceView ? 'Visual' : 'Source'}
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={toggleFullscreen}
                          style={{ fontSize: '12px', padding: '4px 8px' }}
                          title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
                        >
                          <i className={`fa ${isFullscreen ? 'fa-compress' : 'fa-expand'}`} style={{ marginRight: '4px' }}></i>
                          {isFullscreen ? 'Exit' : 'Fullscreen'}
                        </button>
                      </div>
                    </div>
                    <div
                      style={{
                        position: isFullscreen ? 'fixed' : 'relative',
                        top: isFullscreen ? 0 : 'auto',
                        left: isFullscreen ? 0 : 'auto',
                        right: isFullscreen ? 0 : 'auto',
                        bottom: isFullscreen ? 0 : 'auto',
                        width: isFullscreen ? '100vw' : '100%',
                        height: isFullscreen ? '100vh' : 'auto',
                        zIndex: isFullscreen ? 9999 : 'auto',
                        backgroundColor: isFullscreen ? '#fff' : 'transparent',
                        padding: isFullscreen ? '20px' : '0',
                        overflow: isFullscreen ? 'auto' : 'visible'
                      }}
                    >
                      {isSourceView ? (
                        <textarea
                          ref={sourceTextareaRef}
                          className="form-control"
                          value={sourceCode}
                          onChange={handleSourceCodeChange}
                          style={{
                            minHeight: isFullscreen ? 'calc(100vh - 100px)' : '300px',
                            fontFamily: 'monospace',
                            fontSize: '12px',
                            width: '100%'
                          }}
                          placeholder="Paste your HTML source code here..."
                        />
                      ) : (
                        <div style={{ minHeight: isFullscreen ? 'calc(100vh - 3000px)' : '300px' }}>
                          <style>
                            {`
                              .ck-editor__editable {
                                color: #000000 !important;
                              }
                              .ck-editor__editable p,
                              .ck-editor__editable div,
                              .ck-editor__editable span,
                              .ck-editor__editable li,
                              .ck-editor__editable td,
                              .ck-editor__editable th {
                                color: #000000 !important;
                              }
                              .ck.ck-editor__main>.ck-editor__editable:not(.ck-focused) {
                                height: 300px;
                                border-color: var(--ck-color-base-border);
                              }
                              .ck-content {
                                height: 300px;
                              }
                            `}
                          </style>
                          <CKEditor
                            key={editorKey}
                            editor={ClassicEditor}
                            config={editorConfiguration}
                            data={emailFormData.message}
                            onReady={(editor) => {
                              editorRef.current = editor;
                              
                              // Debug: Check available plugins
                              console.log('Available CKEditor plugins:', Object.keys(editor.plugins._plugins));
                              
                              // Check if image plugin is available
                              try {
                                const imagePlugin = editor.plugins.get('Image');
                                console.log('Image plugin found:', imagePlugin);
                              } catch (e) {
                                console.error('Image plugin not found:', e);
                              }
                              
                              // Inject custom upload adapter for base64 image embedding (no API call)
                              uploadAdapterPlugin(editor);
                              
                              // Mark editor as initialized - after this, we won't update the data prop
                              // This makes it uncontrolled and prevents cursor jumps
                              editorInitializedRef.current = true;
                              
                              // Preserve table attributes and cell styles when editor is ready
                              // Get the initial data that was passed to the editor (before CKEditor processed it)
                              const initialData = emailFormData.message || '';
                              if (initialData) {
                                // Preserve attributes and cell styles from the original data (before CKEditor stripped them)
                                preservedTableAttributesRef.current = preserveTableAttributes(initialData);
                                preservedCellStylesRef.current = preserveCellStyles(initialData);
                                originalHtmlWithAttributesRef.current = initialData;
                                
                                // If the initial data has attributes, restore them in the editor
                                const dataFromEditor = editor.getData();
                                const restoredData = restoreTableAttributes(dataFromEditor, preservedTableAttributesRef.current, preservedCellStylesRef.current);
                                if (restoredData !== dataFromEditor && restoredData === initialData) {
                                  // Only update if we have a better version with attributes
                                  editor.setData(restoredData);
                                }
                              }
                            }}
                            onChange={(event, editor) => {
                              const data = editor.getData();
                              
                              // If we're in source view, don't update from editor (use sourceCode instead)
                              if (isSourceView) {
                                return;
                              }
                              
                              // CRITICAL: DO NOT update emailFormData.message here to avoid cursor jumps!
                              // CKEditor manages its own state - we'll get the data on submit
                              // Only store in ref for later use without causing re-renders
                              
                              // Clear any pending timeout
                              if (onChangeTimeoutRef.current) {
                                clearTimeout(onChangeTimeoutRef.current);
                              }
                              
                              // Debounce heavy attribute restoration operations
                              // This prevents cursor jumps and allows normal typing/undo/backspace
                              onChangeTimeoutRef.current = setTimeout(() => {
                                console.log('✏️ ===== PROCESSING EDITOR CONTENT (Debounced) =====');
                                console.log('📝 CKEditor raw data:', data.substring(0, 300));
                                console.log('📏 Data length:', data.length);
                                
                                // Check if we have preserved attributes to restore
                                const hasPreservedAttrs = Object.keys(preservedTableAttributesRef.current).length > 0;
                                const hasPreservedCellStyles = Object.keys(preservedCellStylesRef.current).length > 0;
                                
                                // Only do heavy restoration if we have preserved attributes/styles
                                if (hasPreservedAttrs || hasPreservedCellStyles) {
                                  console.log('💾 Available preserved attributes/styles, restoring...');
                                  
                                  // Restore preserved table attributes and cell styles
                                  let dataWithAttributes = restoreTableAttributes(data, preservedTableAttributesRef.current, preservedCellStylesRef.current);
                                  
                                  // If restoration didn't work, try using original HTML
                                  if (dataWithAttributes === data && originalHtmlWithAttributesRef.current) {
                                    const originalPreserved = preserveTableAttributes(originalHtmlWithAttributesRef.current);
                                    const originalCellStyles = preserveCellStyles(originalHtmlWithAttributesRef.current);
                                    if (Object.keys(originalPreserved).length > 0 || Object.keys(originalCellStyles).length > 0) {
                                      dataWithAttributes = restoreTableAttributes(data, originalPreserved, originalCellStyles);
                                      preservedTableAttributesRef.current = originalPreserved;
                                      preservedCellStylesRef.current = originalCellStyles;
                                    }
                                  }
                                  
                                  // Update preserved attributes/styles from current content (for new tables)
                                  const currentPreserved = preserveTableAttributes(dataWithAttributes);
                                  const currentCellStyles = preserveCellStyles(dataWithAttributes);
                                  
                                  // Merge preserved attributes (prioritize original)
                                  Object.keys(currentPreserved).forEach(key => {
                                    if (preservedTableAttributesRef.current[key]) {
                                      const existing = preservedTableAttributesRef.current[key];
                                      preservedTableAttributesRef.current[key] = { ...existing, ...currentPreserved[key] };
                                    } else {
                                      preservedTableAttributesRef.current[key] = currentPreserved[key];
                                    }
                                  });
                                  
                                  // Merge cell styles (prioritize original)
                                  Object.keys(currentCellStyles).forEach(key => {
                                    if (preservedCellStylesRef.current[key]) {
                                      const existing = preservedCellStylesRef.current[key];
                                      preservedCellStylesRef.current[key] = { ...existing, ...currentCellStyles[key] };
                                    } else {
                                      preservedCellStylesRef.current[key] = currentCellStyles[key];
                                    }
                                  });
                                  
                                  // Final restoration pass
                                  let finalDataWithAttributes = restoreTableAttributes(dataWithAttributes, preservedTableAttributesRef.current, preservedCellStylesRef.current);
                                  
                                  // Update originalHtmlWithAttributesRef silently (without triggering re-render)
                                  originalHtmlWithAttributesRef.current = finalDataWithAttributes;
                                  
                                  // Only update state if the restored version is different and better
                                  // But do it silently without causing editor re-render
                                  if (finalDataWithAttributes !== data && finalDataWithAttributes !== emailFormData.message) {
                                    // Store in ref for use on submit, but don't update state to avoid cursor jump
                                    originalHtmlWithAttributesRef.current = finalDataWithAttributes;
                                    console.log('✅ Attributes restored and stored in ref (will be used on submit)');
                                  }
                                } else {
                                  // No preserved attributes, just update the ref with current data
                                  originalHtmlWithAttributesRef.current = data;
                                }
                                
                                console.log('✏️ ===== END OF PROCESSING =====');
                              }, 500); // 500ms debounce - allows normal typing without interference
                            }}
                          />
                        </div>
                      )}
                      {isFullscreen && (
                        <div style={{
                          position: 'fixed',
                          top: '10px',
                          right: '10px',
                          zIndex: 10000,
                          display: 'flex',
                          gap: '8px'
                        }}>
                          <button
                            type="button"
                            className={`btn btn-sm ${isSourceView ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={toggleSourceView}
                            style={{ fontSize: '12px', padding: '4px 8px' }}
                          >
                            <i className="fa fa-code" style={{ marginRight: '4px' }}></i>
                            {isSourceView ? 'Visual' : 'Source'}
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={toggleFullscreen}
                            style={{ fontSize: '12px', padding: '4px 8px' }}
                          >
                            <i className="fa fa-compress" style={{ marginRight: '4px' }}></i>
                            Exit
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="modal-footer" style={{ borderTop: '1px solid #dee2e6', padding: '15px 20px', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    className="btn"
                    style={{ backgroundColor: '#dc3545', color: '#fff', marginRight: '10px', padding: '8px 20px' }}
                    onClick={() => {
                      // Clear any pending timeout
                      if (onChangeTimeoutRef.current) {
                        clearTimeout(onChangeTimeoutRef.current);
                        onChangeTimeoutRef.current = null;
                      }
                      setShowSendEmailModal(false);
                      setEmailFormData({ fromEmail: '', subject: '', message: '' });
                      setSourceCode('');
                      setIsSourceView(false);
                      editorInitializedRef.current = false;
                      preservedTableAttributesRef.current = {};
                      preservedCellStylesRef.current = {};
                      originalHtmlWithAttributesRef.current = '';
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn"
                    style={{ backgroundColor: '#28a745', color: '#fff', padding: '8px 20px' }}
                    disabled={loading}
                  >
                    {loading ? 'Sending...' : 'Send Emails'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

    </Layout>
  );
}

export default VlsEmails;
